import { Checkbox, Grid, Group, Radio, Select, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { Patient, Address } from '@medplum/fhirtypes';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsTruncated } from '@/hooks/useIsTruncated';
import { useTimelineContext } from '../hooks/useTimelineContext';
import { alert } from '@/services/alert';
import {
  buildPatientCommunication,
  formatBirthDate,
  formatGender,
  formatPatientLanguagesDisplay,
  parsePatientLanguages,
  PATIENT_LANGUAGE_OPTIONS,
  PatientLanguageCode,
} from '@/services/patient';
import { formatAddress } from '@medplum/core';
import { ORBIT_GRAY } from '@/theme/colors';

const GENDER_OPTIONS: { value: NonNullable<Patient['gender']>; label: string }[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'unknown', label: 'Unknown' },
];

interface AboutSectionProps {
  patient: Patient;
}

function StructuredAddressEditor({
  address,
  onGetCurrentAddress,
  onSave,
}: {
  address: Address | undefined;
  onGetCurrentAddress: (getAddress: () => Address) => void;
  onSave: () => void;
}) {
  const [line1, setLine1] = useState<string>(address?.line?.[0] || '');
  const [line2, setLine2] = useState<string>(address?.line?.[1] || '');
  const [city, setCity] = useState<string>(address?.city || '');
  const [state, setState] = useState<string>(address?.state || '');
  const [postalCode, setPostalCode] = useState<string>(address?.postalCode || '');

  useEffect(() => {
    setLine1(address?.line?.[0] || '');
    setLine2(address?.line?.[1] || '');
    setCity(address?.city || '');
    setState(address?.state || '');
    setPostalCode(address?.postalCode || '');
  }, [address]);

  const buildAddress = useCallback((): Address => {
    const lines = [line1, line2].filter(Boolean);
    const addr: Address = {
      use: 'home',
      type: 'both',
    };

    if (lines.length > 0) addr.line = lines;
    if (city) addr.city = city;
    if (state) addr.state = state;
    if (postalCode) addr.postalCode = postalCode;

    return addr;
  }, [line1, line2, city, state, postalCode]);

  useEffect(() => {
    onGetCurrentAddress(buildAddress);
  }, [buildAddress, onGetCurrentAddress]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSave();
    }
  };

  return (
    <Group gap={4} align="flex-start" wrap="wrap" style={{ width: '100%' }}>
      <TextInput
        placeholder="Line 1"
        value={line1}
        onChange={(e) => setLine1(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        size="xs"
        radius="md"
        style={{ flex: '1 1 auto', minWidth: 120 }}
        styles={{ input: { height: 18, minHeight: 18, fontSize: 11 } }}
      />
      <TextInput
        placeholder="Line 2"
        value={line2}
        onChange={(e) => setLine2(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        size="xs"
        radius="md"
        style={{ flex: '1 1 auto', minWidth: 120 }}
        styles={{ input: { height: 18, minHeight: 18, fontSize: 11 } }}
      />
      <TextInput
        placeholder="City"
        value={city}
        onChange={(e) => setCity(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        size="xs"
        radius="md"
        style={{ flex: '1 1 auto', minWidth: 100 }}
        styles={{ input: { height: 18, minHeight: 18, fontSize: 11 } }}
      />
      <TextInput
        placeholder="State"
        value={state}
        onChange={(e) => setState(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        size="xs"
        radius="md"
        style={{ flex: '0 1 auto', minWidth: 80 }}
        styles={{ input: { height: 18, minHeight: 18, fontSize: 11 } }}
      />
      <TextInput
        placeholder="Postal Code"
        value={postalCode}
        onChange={(e) => setPostalCode(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        size="xs"
        radius="md"
        style={{ flex: '0 1 auto', minWidth: 100 }}
        styles={{ input: { height: 18, minHeight: 18, fontSize: 11 } }}
      />
    </Group>
  );
}

function EditableAddress({
  address,
  onUpdate,
}: {
  address: Address | undefined;
  onUpdate: (address: Address) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const getCurrentAddressRef = useRef<(() => Address) | null>(null);
  const isTruncated = useIsTruncated(textRef, [address]);

  useEffect(() => {
    if (!isEditing) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (editorRef.current && !editorRef.current.contains(event.target as Node)) {
        // Get the current address from the editor and save it
        if (getCurrentAddressRef.current) {
          const currentAddress = getCurrentAddressRef.current();
          onUpdate(currentAddress);
        }
        setIsEditing(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, onUpdate]);

  const handleGetCurrentAddress = useCallback((getAddress: () => Address) => {
    getCurrentAddressRef.current = getAddress;
  }, []);

  const handleTextClick = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    if (getCurrentAddressRef.current) {
      const currentAddress = getCurrentAddressRef.current();
      onUpdate(currentAddress);
    }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div ref={editorRef} style={{ width: '100%' }}>
        <StructuredAddressEditor address={address} onGetCurrentAddress={handleGetCurrentAddress} onSave={handleSave} />
      </div>
    );
  }

  const addressText = formatAddress(address);
  const textElement = (
    <Text
      ref={textRef}
      onClick={handleTextClick}
      style={{
        cursor: 'pointer',
        width: '100%',
        minHeight: '18px',
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '20px',
        color: 'var(--mantine-color-black)',
      }}
    >
      {addressText || 'No address'}
    </Text>
  );

  if (isTruncated && addressText) {
    return (
      <Tooltip label={addressText} withArrow position="top">
        {textElement}
      </Tooltip>
    );
  }

  return textElement;
}

function AboutSectionRow({ label, children }: { label: string; children: React.ReactNode }) {
  const labelStyle = { fontSize: 13, lineHeight: '16px', color: ORBIT_GRAY };

  return (
    <Grid align="flex-start" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Grid.Col span={3} style={{ display: 'flex', alignItems: 'flex-start', minHeight: '18px' }}>
        <Text style={labelStyle}>{label}</Text>
      </Grid.Col>
      <Grid.Col span={9} style={{ display: 'flex', alignItems: 'flex-start', minHeight: '18px' }}>
        {children}
      </Grid.Col>
    </Grid>
  );
}

function EditableGender({
  gender,
  onUpdate,
}: {
  gender: Patient['gender'];
  onUpdate: (gender: NonNullable<Patient['gender']>) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing) {
    return (
      <Select
        data={GENDER_OPTIONS}
        value={gender ?? null}
        onChange={(value) => {
          if (value) {
            onUpdate(value as NonNullable<Patient['gender']>);
          }
          setIsEditing(false);
        }}
        onBlur={() => setIsEditing(false)}
        size="xs"
        radius="md"
        autoFocus
        defaultDropdownOpened
        comboboxProps={{ withinPortal: true }}
        styles={{ input: { height: 18, minHeight: 18, fontSize: 11 } }}
        style={{ width: '100%' }}
      />
    );
  }

  return (
    <Text
      onClick={() => setIsEditing(true)}
      style={{
        cursor: 'pointer',
        width: '100%',
        minHeight: '18px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '20px',
        color: 'var(--mantine-color-black)',
      }}
    >
      {formatGender(gender)}
    </Text>
  );
}

function StructuredLanguageEditor({
  communication,
  onGetCurrentCommunication,
}: {
  communication: Patient['communication'];
  onGetCurrentCommunication: (getCommunication: () => Patient['communication']) => void;
}) {
  const parsed = parsePatientLanguages(communication);
  const [selectedCodes, setSelectedCodes] = useState<PatientLanguageCode[]>(parsed.selectedCodes);
  const [preferredCode, setPreferredCode] = useState<PatientLanguageCode | undefined>(parsed.preferredCode);

  useEffect(() => {
    const next = parsePatientLanguages(communication);
    setSelectedCodes(next.selectedCodes);
    setPreferredCode(next.preferredCode);
  }, [communication]);

  const buildCommunication = useCallback((): Patient['communication'] => {
    return buildPatientCommunication(selectedCodes, preferredCode);
  }, [selectedCodes, preferredCode]);

  useEffect(() => {
    onGetCurrentCommunication(buildCommunication);
  }, [buildCommunication, onGetCurrentCommunication]);

  const handleSelectedChange = (values: string[]) => {
    const codes = values.filter((value): value is PatientLanguageCode =>
      PATIENT_LANGUAGE_OPTIONS.some((option) => option.code === value)
    );
    setSelectedCodes(codes);

    if (codes.length === 1) {
      setPreferredCode(codes[0]);
      return;
    }

    if (preferredCode && codes.includes(preferredCode)) {
      return;
    }

    setPreferredCode(codes[0]);
  };

  return (
    <Stack gap={8} style={{ width: '100%' }}>
      <Checkbox.Group value={selectedCodes} onChange={handleSelectedChange}>
        <Stack gap={4}>
          {PATIENT_LANGUAGE_OPTIONS.map((option) => (
            <Checkbox key={option.code} value={option.code} label={option.display} size="xs" />
          ))}
        </Stack>
      </Checkbox.Group>

      {selectedCodes.length > 1 && (
        <Radio.Group
          label="Preferred"
          value={preferredCode}
          onChange={(value) => setPreferredCode(value as PatientLanguageCode)}
          size="xs"
        >
          <Stack gap={4}>
            {selectedCodes.map((code) => {
              const option = PATIENT_LANGUAGE_OPTIONS.find((item) => item.code === code);
              return <Radio key={code} value={code} label={option?.display ?? code} size="xs" />;
            })}
          </Stack>
        </Radio.Group>
      )}
    </Stack>
  );
}

function EditableLanguages({
  communication,
  onUpdate,
}: {
  communication: Patient['communication'];
  onUpdate: (communication: Patient['communication']) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const getCurrentCommunicationRef = useRef<(() => Patient['communication']) | null>(null);
  const displayText = formatPatientLanguagesDisplay(communication);
  const isTruncated = useIsTruncated(textRef, [displayText]);

  useEffect(() => {
    if (!isEditing) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (editorRef.current && !editorRef.current.contains(event.target as Node)) {
        if (getCurrentCommunicationRef.current) {
          onUpdate(getCurrentCommunicationRef.current());
        }
        setIsEditing(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, onUpdate]);

  const handleGetCurrentCommunication = useCallback((getCommunication: () => Patient['communication']) => {
    getCurrentCommunicationRef.current = getCommunication;
  }, []);

  if (isEditing) {
    return (
      <div ref={editorRef} style={{ width: '100%' }}>
        <StructuredLanguageEditor
          communication={communication}
          onGetCurrentCommunication={handleGetCurrentCommunication}
        />
      </div>
    );
  }

  const textElement = (
    <Text
      ref={textRef}
      onClick={() => setIsEditing(true)}
      style={{
        cursor: 'pointer',
        width: '100%',
        minHeight: '18px',
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '20px',
        color: 'var(--mantine-color-black)',
      }}
    >
      {displayText}
    </Text>
  );

  if (isTruncated && displayText !== 'No languages') {
    return (
      <Tooltip label={displayText} withArrow position="top">
        {textElement}
      </Tooltip>
    );
  }

  return textElement;
}

export function AboutSection({ patient: patientProp }: AboutSectionProps) {
  const { patientContext } = useTimelineContext();
  const patientFromContext = patientContext?.patient;
  const updatePatient = patientContext?.updatePatient;

  const patient = patientFromContext || patientProp;
  const primaryAddress = patient.address?.[0];

  const handleAddressUpdate = async (updatedAddress: Address) => {
    if (!updatePatient) return;

    const currentPatient = patientFromContext || patientProp;
    const addresses = currentPatient.address ? [...currentPatient.address] : [];

    // Replace first address or add if none exists
    addresses[0] = updatedAddress;

    await updatePatient({ address: addresses });
  };

  const handleGenderUpdate = async (updatedGender: NonNullable<Patient['gender']>) => {
    if (!updatePatient) return;
    await updatePatient({ gender: updatedGender });
  };

  const handleLanguageUpdate = async (communication: Patient['communication']) => {
    if (!updatePatient) return;

    try {
      await updatePatient({ communication });
    } catch {
      alert.error('Failed to update patient languages. Please try again.');
    }
  };

  const valueStyle = { fontSize: 14, lineHeight: '20px', fontWeight: 400, color: 'var(--mantine-color-black)' };

  return (
    <Stack gap={8}>
      <AboutSectionRow label="DoB">
        <Text style={valueStyle}>{formatBirthDate(patient.birthDate)}</Text>
      </AboutSectionRow>

      <AboutSectionRow label="Gender">
        <EditableGender gender={patient.gender} onUpdate={handleGenderUpdate} />
      </AboutSectionRow>

      <AboutSectionRow label="Address">
        <EditableAddress address={primaryAddress} onUpdate={handleAddressUpdate} />
      </AboutSectionRow>

      <AboutSectionRow label="Languages">
        <EditableLanguages communication={patient.communication} onUpdate={handleLanguageUpdate} />
      </AboutSectionRow>
    </Stack>
  );
}
